// frontend/src/components/handbook/HandbookSearch.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, BookOpen, CheckSquare, Command } from "lucide-react";

const buildSearchIndex = (handbook) => {
  if (!handbook?.sections) return [];
  const idx = [];
  for (const section of handbook.sections) {
    for (const chapter of section.chapters) {
      idx.push({
        type: "chapter",
        chapterSlug: chapter.slug,
        sectionTitle: section.title,
        title: chapter.title,
        subtitle: chapter.subtitle,
        haystack: `${chapter.title} ${chapter.subtitle || ""}`.toLowerCase(),
      });
      for (const cl of chapter.checklists || []) {
        for (const item of cl.items || []) {
          idx.push({
            type: "item",
            chapterSlug: chapter.slug,
            chapterTitle: chapter.title,
            sectionTitle: section.title,
            itemKey: item.key,
            text: item.text,
            checklistTitle: cl.title,
            haystack: `${item.text} ${cl.title || ""}`.toLowerCase(),
          });
        }
      }
    }
  }
  return idx;
};

const HandbookSearch = ({ handbook, isOpen, onClose, onOpen }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const index = useMemo(() => buildSearchIndex(handbook), [handbook]);

  const results = useMemo(() => {
    if (query.trim().length < 2) return [];
    const q = query.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    return index
      .filter((entry) => tokens.every((t) => entry.haystack.includes(t)))
      .slice(0, 12);
  }, [index, query]);

  // Cmd/Ctrl+K to open, ESC to close
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpen?.();
      } else if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const goToResult = (entry) => {
    if (!entry) return;
    const path = `/coach-handbook/${entry.chapterSlug}`;
    if (entry.type === "item") {
      navigate(`${path}#${entry.itemKey}`);
    } else {
      navigate(path);
    }
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      goToResult(results[activeIndex]);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center px-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Suche in Kapiteln und Checklisten…"
            className="flex-1 px-3 py-4 text-base outline-none bg-transparent placeholder-gray-400"
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            className="p-2 text-gray-400 hover:text-gray-700 rounded-md hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              <Command className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              Tippe mind. 2 Zeichen, um zu suchen
              <div className="mt-3 text-xs">
                Pfeiltasten zum Navigieren · Enter zum Öffnen · ESC zum Schliessen
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              Keine Treffer für "{query}"
            </div>
          ) : (
            <ul className="py-2">
              {results.map((entry, idx) => {
                const isActive = idx === activeIndex;
                return (
                  <li key={`${entry.type}-${entry.chapterSlug}-${entry.itemKey || ""}-${idx}`}>
                    <button
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => goToResult(entry)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                        isActive ? "bg-[#002855] text-white" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        {entry.type === "chapter" ? (
                          <BookOpen
                            className={`w-5 h-5 ${
                              isActive ? "text-white" : "text-[#00A3E0]"
                            }`}
                          />
                        ) : (
                          <CheckSquare
                            className={`w-5 h-5 ${
                              isActive ? "text-white" : "text-[#84BD00]"
                            }`}
                          />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        {entry.type === "chapter" ? (
                          <>
                            <div className="font-medium truncate">
                              {entry.title}
                            </div>
                            <div
                              className={`text-xs truncate ${
                                isActive ? "text-white/80" : "text-gray-500"
                              }`}
                            >
                              {entry.sectionTitle}
                              {entry.subtitle ? ` · ${entry.subtitle}` : ""}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm leading-snug">
                              {entry.text}
                            </div>
                            <div
                              className={`text-xs truncate mt-0.5 ${
                                isActive ? "text-white/80" : "text-gray-500"
                              }`}
                            >
                              {entry.chapterTitle}
                              {entry.checklistTitle
                                ? ` › ${entry.checklistTitle}`
                                : ""}
                            </div>
                          </>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="hidden sm:flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span>↑↓ Navigation</span>
            <span>↵ Öffnen</span>
            <span>esc Schliessen</span>
          </div>
          <div className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">
              ⌘
            </kbd>
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-gray-700 font-mono">
              K
            </kbd>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HandbookSearch;
